import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { productAPI } from '../../services/api';

export default function Navbar() {
  const { user, logout, isAdmin, loading } = useAuth();
  const { cart }                           = useCart();
  const { count: wishlistCount }           = useWishlist();
  const navigate                           = useNavigate();
  const { pathname }                       = useLocation();
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [search,       setSearch]       = useState('');
  const [showSearch,   setShowSearch]   = useState(false);
  const [suggestions,  setSuggestions]  = useState([]);
  const [sugLoading,   setSugLoading]   = useState(false);
  const [sugFocused,   setSugFocused]   = useState(false);
  const debounceRef  = useRef(null);
  const searchRef    = useRef(null);

  useEffect(() => { setMenuOpen(false); setShowSearch(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const q = search.trim();
    setSuggestions([]); setSugFocused(false); setShowSearch(false);
    navigate(q ? `/browse?search=${encodeURIComponent(q)}` : '/browse');
    setSearch('');
  };

  const fetchSuggestions = useCallback((val) => {
    clearTimeout(debounceRef.current);
    if (!val || val.length < 2) { setSuggestions([]); return; }
    setSugLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await productAPI.suggest(val);
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
      finally { setSugLoading(false); }
    }, 250);
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    fetchSuggestions(val);
  };

  const pickSuggestion = (s) => {
    setSuggestions([]); setSugFocused(false); setShowSearch(false); setSearch('');
    if (s.type === 'product') navigate(`/product/${s.id}`);
    else navigate(`/browse?category=${encodeURIComponent(s.label)}`);
  };

  const handleLogout = async () => {
    setLoggingOut(true); setMenuOpen(false);
    await logout(true);
    navigate('/', { replace: true });
    setLoggingOut(false);
  };

  return (
    <>
      <header style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', position: 'sticky', top: 0, zIndex: 200 }}>

        {/* ── MAIN BAR ── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,3vw,24px)', height: 64, display: 'flex', alignItems: 'center', gap: 8 }}>

          {/* Left — desktop nav links only */}
          <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
            {!loading && !isAdmin && (
              <>
                <NavLink to="/browse"  active={pathname.startsWith('/browse')}>Shop</NavLink>
                <NavLink to="/brands"  active={pathname === '/brands'}>For Brands</NavLink>
                {user && <NavLink to="/orders" active={pathname === '/orders'}>Orders</NavLink>}
                {user && <NavLink to="/trust" active={pathname === '/trust'}>Score</NavLink>}
              </>
            )}
            {!loading && isAdmin && (
              <NavLink to="/admin" active={pathname.startsWith('/admin')}>Admin</NavLink>
            )}
          </div>

          {/* Brand — left on mobile, centre on desktop */}
          <Link to="/"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(20px,4vw,30px)', color: '#111', letterSpacing: '0.06em', textDecoration: 'none', flexShrink: 0, lineHeight: 1, whiteSpace: 'nowrap',
              flex: '0 0 auto', marginRight: 'auto' }}
            className="brand-link">
            Warb<span style={{ color: '#e8a838' }}>adobe</span><span style={{ fontSize: '0.42em', letterSpacing: '0.2em', textTransform: 'uppercase', marginLeft: 6, color: '#999', fontFamily: "'Josefin Sans',sans-serif" }}> Rentals</span>
          </Link>

          {/* Right — icons */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0 }}>
            {/* Search — always visible */}
            <IconBtn onClick={() => setShowSearch(s => !s)} title="Search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </IconBtn>

            {loading ? null : user ? (
              <>
                {!isAdmin && (
                  <>
                    {/* Wishlist — desktop only (in hamburger on mobile) */}
                    <NavIconLink to="/wishlist" badge={wishlistCount} title="Wishlist" className="hide-mobile">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </NavIconLink>
                    {/* Cart — always visible */}
                    <NavIconLink to="/cart" badge={cart.itemCount} title="Cart">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                    </NavIconLink>
                    <NavIconLink to="/profile" title="Profile" className="hide-mobile">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </NavIconLink>
                  </>
                )}
                <button onClick={handleLogout} disabled={loggingOut} className="hide-mobile"
                  style={{ fontSize: 11, color: '#888', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 8px', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#111'}
                  onMouseLeave={e => e.currentTarget.style.color='#888'}>
                  {loggingOut ? '...' : 'Logout'}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="hide-mobile"
                  style={{ fontSize: 11, color: '#666', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 8px', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#111'}
                  onMouseLeave={e => e.currentTarget.style.color='#666'}>
                  Sign In
                </Link>
                <Link to="/register" className="hide-mobile"
                  style={{ fontSize: 11, background: '#111', color: '#fff', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '8px 16px', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#e8a838'}
                  onMouseLeave={e => e.currentTarget.style.background='#111'}>
                  Join
                </Link>
              </>
            )}

            {/* Hamburger — mobile only */}
            <button className="hide-desktop" onClick={() => setMenuOpen(m => !m)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 5, padding: '8px', minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 2 }}>
              {[0,1,2].map(i => (
                <span key={i} style={{ display: 'block', width: 20, height: 1.5, background: '#111', transition: 'all 0.25s', transformOrigin: 'center',
                  transform: menuOpen ? (i===0?'translateY(6.5px) rotate(45deg)':i===2?'translateY(-6.5px) rotate(-45deg)':'scaleX(0)'):'none',
                  opacity: menuOpen && i===1 ? 0 : 1 }} />
              ))}
            </button>
          </div>
        </div>

        {/* ── SEARCH BAR (dropdown) ── */}
        {showSearch && (
          <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px clamp(12px,3vw,24px)', background: '#fff' }}>
            <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto' }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', border: '2px solid #e8a838', overflow: 'hidden' }}>
                <input
                  ref={searchRef}
                  autoFocus
                  value={search}
                  onChange={e => handleSearchChange(e.target.value)}
                  onFocus={() => setSugFocused(true)}
                  onBlur={() => setTimeout(() => setSugFocused(false), 180)}
                  placeholder="Search outfits, saree, blazer, kurti..."
                  style={{ flex: 1, border: 'none', outline: 'none', padding: '10px 14px', fontSize: 13, background: '#fff', color: '#111' }}
                />
                <button type="submit" style={{ background: '#e8a838', border: 'none', padding: '10px 16px', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', flexShrink: 0 }}>
                  Search
                </button>
              </form>

              {/* Suggestions dropdown */}
              {sugFocused && (suggestions.length > 0 || sugLoading) && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e8e8e8', borderTop: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 999, maxHeight: 320, overflowY: 'auto' }}>
                  {sugLoading && !suggestions.length && (
                    <div style={{ padding: '12px 16px', fontSize: 12, color: '#aaa' }}>Searching...</div>
                  )}
                  {suggestions.map((s, i) => (
                    <div key={i} onMouseDown={() => pickSuggestion(s)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fffbf2'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      {/* Icon */}
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: s.type === 'product' ? '#f5f5f5' : '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {s.type === 'product'
                          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e8a838" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: '#111', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{s.sub}</div>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  ))}
                  {/* Full search option */}
                  {search.trim() && (
                    <div onMouseDown={handleSearch}
                      style={{ padding: '10px 16px', fontSize: 12, color: '#e8a838', fontWeight: 600, cursor: 'pointer', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fffbf2'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e8a838" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                      Search all results for "{search}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SECONDARY NAV: category strip ── */}
        <div style={{ borderTop: '1px solid #f0f0f0', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }} className="hide-mobile">
          <div style={{ display: 'flex', justifyContent: 'center', maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,3vw,24px)' }}>
            {[
              { label: 'All',           to: '/browse' },
              { label: 'Formal',        to: '/browse?category=Formal' },
              { label: 'Party',         to: '/browse?category=Party' },
              { label: 'Traditional',   to: '/browse?category=Traditional' },
              { label: 'Casual',        to: '/browse?category=Casual' },
              { label: 'Ethnic',        to: '/browse?category=Ethnic' },
              { label: 'Streetwear',    to: '/browse?category=Streetwear' },
              { label: 'Luxury Evening',to: '/browse?category=Luxury Evening' },
              { label: 'Western',       to: '/browse?category=Western' },
              { label: 'Fusion',        to: '/browse?category=Fusion' },
            ].map(item => (
              <Link key={item.label} to={item.to}
                style={{ fontSize: 11, color: '#555', textDecoration: 'none', padding: '9px 14px', whiteSpace: 'nowrap', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '2px solid transparent', transition: 'all 0.15s', display: 'block' }}
                onMouseEnter={e => { e.currentTarget.style.color='#111'; e.currentTarget.style.borderBottomColor='#111'; }}
                onMouseLeave={e => { e.currentTarget.style.color='#555'; e.currentTarget.style.borderBottomColor='transparent'; }}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      {menuOpen && <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 299 }} />}
      <div style={{ position: 'fixed', top: 0, right: 0, width: 'min(280px,82vw)', height: '100dvh', background: '#fff', zIndex: 300, display: 'flex', flexDirection: 'column', transition: 'transform 0.3s ease', transform: menuOpen ? 'translateX(0)' : 'translateX(100%)', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: '#111' }}>
            Warb<span style={{ color: '#e8a838' }}>adobe</span>
          </span>
          <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', minWidth: 40, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {user && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 10, background: '#fafafa' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e8a838', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{user.name?.[0]?.toUpperCase()}</div>
            <div>
              <div style={{ fontSize: 13, color: '#111', fontWeight: 600 }}>{user.name}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>{user.email}</div>
            </div>
          </div>
        )}

        <div style={{ flex: 1 }}>
          {[
            { label: 'Shop All',    to: '/browse',   show: true },
            { label: 'My Orders',   to: '/orders',   show: !!user && !isAdmin },
            { label: 'Warba Score',  to: '/trust',    show: !!user && !isAdmin },
            { label: 'Profile',     to: '/profile',  show: !!user && !isAdmin },
            { label: 'Wishlist',    to: '/wishlist',  show: !!user && !isAdmin },
            { label: 'Cart',        to: '/cart',      show: !!user && !isAdmin },
            { label: 'Admin Panel', to: '/admin',     show: !!isAdmin },
            { label: 'Sign In',     to: '/login',     show: !user },
            { label: 'Create Account', to: '/register', show: !user },
          ].filter(i => i.show).map(item => (
            <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', color: '#333', fontSize: 13, textDecoration: 'none', borderBottom: '1px solid #f5f5f5', letterSpacing: '0.04em' }}>
              {item.label}
              <span style={{ color: '#ccc' }}>›</span>
            </Link>
          ))}
        </div>

        {user && (
          <button onClick={handleLogout} style={{ margin: '16px 20px 32px', background: 'none', border: '1px solid #e0e0e0', color: '#888', cursor: 'pointer', padding: '12px', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {loggingOut ? 'Logging out...' : 'Logout'}
          </button>
        )}
      </div>
    </>
  );
}

function NavLink({ to, active, children }) {
  return (
    <Link to={to} style={{ fontSize: 11, color: active ? '#111' : '#666', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: active ? 600 : 400, borderBottom: active ? '1px solid #111' : '1px solid transparent', paddingBottom: 1, transition: 'all 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.color='#111'}
      onMouseLeave={e => e.currentTarget.style.color = active ? '#111' : '#666'}>
      {children}
    </Link>
  );
}

function IconBtn({ onClick, title, children }) {
  return (
    <button onClick={onClick} title={title}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', minWidth: 36, minHeight: 36, borderRadius: 2, transition: 'color 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.color='#e8a838'}
      onMouseLeave={e => e.currentTarget.style.color='#333'}>
      {children}
    </button>
  );
}

function NavIconLink({ to, badge, title, children, className }) {
  return (
    <Link to={to} title={title} className={className}
      style={{ position: 'relative', color: '#333', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', minWidth: 36, minHeight: 36, borderRadius: 2, transition: 'color 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.color='#e8a838'}
      onMouseLeave={e => e.currentTarget.style.color='#333'}>
      {children}
      {badge > 0 && (
        <span style={{ position: 'absolute', top: 2, right: 2, background: '#e8a838', color: '#fff', borderRadius: '50%', width: 15, height: 15, fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{badge}</span>
      )}
    </Link>
  );
}